import React, { useState, useEffect, useCallback, useRef } from 'react';

// Material-UI Core Components
import { 
    makeStyles, 
    createTheme, 
    Typography, 
    Box 
} from '@material-ui/core';

// Material-UI DataGrid
import { DataGrid, esES } from '@material-ui/data-grid';

// External Libraries
import Cookies from 'universal-cookie';
import { useHome } from './HomeContext';

// Estilos
const defaultTheme = createTheme();
const useStyles = makeStyles((theme) => ({
    root: {
        [theme.breakpoints.down('sm')]: {
            height: 150,
            '& .MuiDataGrid-root': {
                maxHeight: 120, 
            },
        },
        [theme.breakpoints.up('md')]: {
            height: 200,
            '& .MuiDataGrid-root': {
                maxHeight: 200, 
            },
        },
        [theme.breakpoints.up('lg')]: {
            height: 270,
            '& .MuiDataGrid-root': {
                maxHeight: 243,
                '& .MuiDataGrid-viewport': {
                    maxHeight: '183px !important', 
                },
            },
        },
        [theme.breakpoints.up('xl')]: {
            height: 337,
            '& .MuiDataGrid-root': {
                maxHeight: 253, 
            },
        },
        width: '100%',
        overflow: 'auto',
    },
    errorMessage: {
        color: theme.palette.error.main,
        padding: theme.spacing(2),
        textAlign: 'center'
    }
}), { defaultTheme });

export default function TablaANDON() {
    const classes = useStyles();
    const [rowsProxCorridas, setRowsProxCorridas] = useState([]);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    
    const { isCorridaActive, detailOrder, selectOrder } = useHome();

    const cookie = new Cookies();
    const linea = parseInt(cookie.get('linea'));
    const tokenSessionObject = JSON.parse(sessionStorage.getItem('token'));
    
    // Refs para controlar la frecuencia de llamadas
    const lastFetchTime = useRef(0);
    const fetchTimeoutRef = useRef(null);
    const MIN_TIME_BETWEEN_CALLS = 5000; // 5 segundos mínimo entre llamadas

    const filtrarDatos = useCallback((data, activeCorrida) => {
        if (typeof data === 'string') return [];
        if (!Array.isArray(data)) return [];

        return data
            .filter((item) => !activeCorrida || item.corrida !== activeCorrida)
            .map((item, index) => ({
                id: item.id || item.corrida || index,
                ordenProd: item.ordenProd ?? 'N/A',
                nombre: item.nombre ?? 'N/A',
                meta: item.meta ?? 0,
                sku: item.sku ?? 'N/A',
                corrida: item.corrida ?? 'N/A',
                cliente: item.cliente ?? 'N/A',
            }));
    }, []);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();
        
        const performFetch = async () => {
            if (!tokenSessionObject || !linea) {
                setError('No hay token de sesión o línea definida');
                return;
            }
            
            setIsLoading(true); 
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_SERVICE_ENDPOINT_NE}/PisoOrdenes/GetOrdenes?linea=${linea}`, 
                    {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${tokenSessionObject.token}`,
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                        },
                        signal: controller.signal
                    }
                );

                if (!response.ok) {
                    throw new Error(`Error en la respuesta del servidor: ${response.status}`);
                }

                const data = await response.json();
                
                if (isMounted) {
                    // El servicio puede devolver un string de error o un array vacío
                    if (data === null || data === undefined) {
                        throw new Error('No se recibieron datos del servidor');
                    }
                    
                    // Excluir la corrida en ejecución — selectOrder se setea antes que detailOrder
                    const activeCorrida = isCorridaActive ? (selectOrder || detailOrder.corrida) : null;
                    const filteredData = filtrarDatos(data, activeCorrida);
                    setRowsProxCorridas(filteredData);
                    setError(null);
                }
            } catch (err) {
                if (isMounted && err.name !== 'AbortError') {
                    console.error('Error al obtener datos:', err);
                    setError(err.message);
                    setRowsProxCorridas([]);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false); 
                }
            }
        };
        
        const fetchData = () => {
            const now = Date.now();
            const timeSinceLastFetch = now - lastFetchTime.current;
            
            // Si pasó menos tiempo del mínimo, programar la llamada para más tarde
            if (timeSinceLastFetch < MIN_TIME_BETWEEN_CALLS) {
                const delay = MIN_TIME_BETWEEN_CALLS - timeSinceLastFetch;
                
                // Limpiar timeout anterior si existe
                if (fetchTimeoutRef.current) {
                    clearTimeout(fetchTimeoutRef.current);
                }
                
                fetchTimeoutRef.current = setTimeout(() => {
                    lastFetchTime.current = Date.now();
                    performFetch();
                }, delay);
            } else {
                // Actualizar el tiempo de la última llamada y hacer la llamada inmediatamente
                lastFetchTime.current = now;
                performFetch();
            }
        };

        fetchData();
        
        return () => {
            isMounted = false;
            controller.abort();
            // Limpiar timeout si existe
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, [tokenSessionObject, linea, filtrarDatos, isCorridaActive, selectOrder, detailOrder]);

    const tableColumns = React.useMemo(() => [
        {
            field: 'ordenProd',
            headerName: 'Orden',
            flex: 1.5,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
        },
        {
            field: 'nombre',
            headerName: 'Nombre',
            flex: 4,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
        },
        {
            field: 'meta',
            headerName: 'Meta',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
        },
        {
            field: 'sku',
            headerName: 'SKU',
            flex: 1.5,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
        },
        {
            field: 'corrida',
            headerName: 'Corrida',
            flex: 1.5,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
        },
        {
            field: 'cliente',
            headerName: 'Cliente',
            flex: 5,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
        },
    ], []);

    const CustomNoRowsOverlay = ({ isLoading }) => (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center',
            }}
        >
            <Typography variant="h6" color="textSecondary">
                {isLoading
                    ? 'Cargando próximas corridas...'
                    : 'No hay próximas corridas agendadas'}
            </Typography>
        </Box>
    );

    if (error) {
        return <Typography className={classes.errorMessage}>{error}</Typography>;
    }

    return (
        <div className={classes.root}>
            <DataGrid
                disableColumnFilter
                disableDensitySelector
                disableColumnMenu
                checkboxSelection={false}
                columns={tableColumns.map(column => ({
                    ...column,
                    sortable: true, 
                }))}
                localeText={esES.props.MuiDataGrid.localeText}
                rows={rowsProxCorridas}
                hideFooterPagination
                hideFooter
                components={{
                    NoRowsOverlay: () => <CustomNoRowsOverlay isLoading={isLoading} />,
                }}
            />
        </div>
    );
}